import { createFormContext, isNotEmpty, UseFormReturnType } from '@mantine/form';
import { FormValidateInput } from '@mantine/form/lib/types';
import { randomId } from '@mantine/hooks';
import {
  EnumerationDto,
  ExpensesType,
  FollowUpsDto,
  KeycloakUserDto,
  ProjectExpenseDto,
  ProjectResponseDto
} from '../../build/generated-sources/openapi';
import {
  getExpensesYears,
  getSustainabilityTableYears,
  stringToDate,
  stringToDateTime
} from '../../core/util/Util';
import isValidIc, { isValidEmail } from '../../core/util/ValidationUtil';

/**
 * [DAP-032] Mantine `NumberInput` volá `onChange` s `number | string` — prázdné pole pošle `''`,
 * rozepsanou hodnotu jako řetězec. Typ `onChange?: (event: any) => void` v
 * `CustomNumberInput.model.ts` to před TypeScriptem schová, takže se řetězce dostanou do výpočtů
 * a `+` je zřetězí místo sečtení ("1" + "1" = "11"). Každou částku výdaje proto normalizujeme.
 */
export const toExpenseNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export type ProjectCardProps = {
  data?: ProjectResponseDto;
  handleRefresh?: () => void;
};

export type ProjectCardFormProps = {
  form: UseFormReturnType<ProjectCardFormValues>;
  isUpdateProject?: boolean;
  isLoading?: boolean;
  combinedExpenses: Map<number, ProjectExpenseDto[]>;
  updateCombinedExpenses: (year: number, yearDataCopy: ProjectExpenseDto[]) => void;
  updateExpensesData: (expensesTotal: ExpensesTotal) => void;
  onFormSubmit?: (
    values: ReturnType<(values: ProjectCardFormValues) => ProjectCardFormValues>,
    event: React.FormEvent<HTMLFormElement> | undefined
  ) => void;
  enumerations?: EnumerationDto[];
  followUps?: FollowUpsDto;
  createdBy?: KeycloakUserDto;
  createdByLegacy?: string;
  isLocked?: boolean;
  disabled?: boolean;
  isPrint?: boolean;
};

export type ExpensesTotal = {
  considerableExpensesTotal: number; // uznatelne
  ineligibleExpensesTotal: number; // neuznatelna
  budgetEntitlementTotal: number; // narok na rozpocet
  operatingCostsTotal: number; // provozni naklady
  sustainabilityCostsTotal: number; // naklady na udrzitelnost
  subsidyReturnTotal: number; // navratnost dotace
  scheduledDepositsReceivedTotal: number; // planovane prijate zalohy
  totalExpenses: number;
  totalBudgetEntitlement: number;
  europeanTotal: number;
  stateBudgetTotal: number;
  otherForeignSourcesTotal: number;
  ownResourcesTotal: number;
  investmentTotal: number;
  nonInvestmentTotal: number;
};

export enum FundingInputMode {
  PERCENT = 'PERCENT',
  NOMINAL = 'NOMINAL'
}

export enum DevelopmentState {
  PLANNING_PHASE = 'PLANNING_PHASE',
  READY_FOR_REALIZATION = 'READY_FOR_REALIZATION',
  REALIZATION = 'REALIZATION',
  REALIZATION_COMPLETE = 'REALIZATION_COMPLETE',
  OTHER = 'OTHER'
}

export interface ProjectCardFormValues {
  name?: string;
  registrationNumber?: string;
  eventNumber?: string;
  holder?: string;
  holderIc?: string;
  holderLegalFormEnumerationId?: string;
  guarantor?: string;
  contactPerson?: string;
  contactEmails?: { key: string; email?: string }[];
  indicators?: {
    key: string;
    id?: number;
    description?: string;
    measuringUnit?: string;
    value?: string;
  }[];
  manager?: string;
  briefDescription?: string;
  partners?: string;
  totalCosts?: number;
  followUpPrkkPriorityAreaId?: string;
  followUpPrkkSubregionId?: string;
  followUpPrkkSpecificTargetId?: string;
  followUpPrkkMeasureId?: string;
  followUpSrrcrStrategicTargetId?: string;
  followUpSrrcrSpecificTargetId?: string;
  followUpSrrcrTypeMeasureId?: string;
  actionPlanEnumerationId?: string;
  developmentStateEnumerationId?: string;
  result?: string;
  resultQuantification?: string;
  typeEnumerationId?: string;
  realizationPlace?: string;
  preprojectPreparationStart?: Date;
  implementationLength?: number;
  territorialDecisionEnumerationId?: string;
  financialEnd?: Date;
  buildingPermitEnumerationId?: string;
  sustainabilityPeriodStart?: Date;
  sustainabilityPeriodEnd?: Date;
  implementationStart?: Date;
  implementationEnd?: Date;
  initiativeEnumerationId?: string;
  totalEstimatedCosts?: number;
  preparationCosts?: number;
  implementationCosts?: number;
  fundingMainSourceEnumerationId?: string;
  notes?: string;
  keyWord?: string;
  operationalProgram?: string;
  priorityAxis?: string;
  investmentPriority?: string;
  territorialDimension?: string;
  european?: number;
  stateBudget?: number;
  otherForeignSources?: number;
  lumpSum?: number;
  ownResourcesIneligibleAmount?: number;
  ownResources?: number;
  created?: Date;
  createdBy?: string;
  updated?: Date;
  updatedBy?: string;
  priority?: boolean;
  financingMethod?: string;
  fundingInputMode?: FundingInputMode;
  financialApproval?: boolean;
  financialApprovalResolution?: string;
  projectPreparationApproval?: boolean;
  projectPreparationResolution?: string;
  otherTypeApproval?: boolean;
  otherTypeResolution?: string;
  legalAct?: boolean;
  legalActDate?: Date;
}

export const projectCardValidation: FormValidateInput<ProjectCardFormValues> = {
  name: isNotEmpty('Název musí být vyplněn'),
  holderIc: (value) => {
    if (!value || value.length === 0) {
      return 'IČ Nositele musí být vyplněn';
    } else if (!isValidIc(value)) {
      return 'Zadané IČO je neplatné';
    }
    return null;
  },
  holder: isNotEmpty('Název nositele projektu musí být vyplněn'),
  holderLegalFormEnumerationId: isNotEmpty('Právní forma nositele projektu musí být vyplněna'),
  guarantor: isNotEmpty('Garant musí být vyplněn'),
  contactPerson: isNotEmpty('Kontaktní osoba/tel musí být vyplněna'),
  contactEmails: {
    email: (value) => {
      if (!value || value.length === 0) {
        return 'Kontaktní email musí být vyplněn';
      } else if (!isValidEmail(value)) {
        return 'Zadaná emailová adresa je neplatná';
      }
      return null;
    }
  },
  briefDescription: isNotEmpty('Stručný popis projektu musí být vyplněn'),
  typeEnumerationId: isNotEmpty('Typ projektu musí být vyplněn'),
  realizationPlace: isNotEmpty('Místo realizace musí být vyplněno'),
  developmentStateEnumerationId: isNotEmpty(
    'Stav rozpracovanosti navrhovaného projektu musí být vyplněn'
  ),
  implementationStart: isNotEmpty('Zahájení realizace musí být vyplněno'),
  implementationEnd: (value, values) =>
    !value || (values.implementationStart && value < values.implementationStart)
      ? 'Ukončení realizace musí být vyplněno a musí být po datu zahájení realizace'
      : null,
  totalEstimatedCosts: isNotEmpty('Celkové (předpokládané) náklady projektu musí být vyplněny')
};

export const projectCardDefaultValues: ProjectCardFormValues = {
  name: '',
  registrationNumber: '',
  eventNumber: '',
  holder: '',
  holderIc: '',
  holderLegalFormEnumerationId: undefined,
  guarantor: '',
  contactPerson: '',
  contactEmails: [{ key: randomId() }],
  indicators: Array.from({ length: 3 }, () => ({ key: randomId() })),
  manager: '',
  briefDescription: '',
  partners: '',
  totalCosts: undefined,
  followUpPrkkPriorityAreaId: undefined,
  followUpPrkkSubregionId: undefined,
  followUpPrkkSpecificTargetId: undefined,
  followUpPrkkMeasureId: undefined,
  followUpSrrcrStrategicTargetId: undefined,
  followUpSrrcrSpecificTargetId: undefined,
  followUpSrrcrTypeMeasureId: undefined,
  actionPlanEnumerationId: undefined,
  developmentStateEnumerationId: undefined,
  result: '',
  resultQuantification: '',
  typeEnumerationId: undefined,
  realizationPlace: '',
  preprojectPreparationStart: undefined,
  implementationLength: undefined,
  territorialDecisionEnumerationId: undefined,
  financialEnd: undefined,
  buildingPermitEnumerationId: undefined,
  sustainabilityPeriodStart: undefined,
  sustainabilityPeriodEnd: undefined,
  implementationStart: undefined,
  implementationEnd: undefined,
  initiativeEnumerationId: undefined,
  totalEstimatedCosts: undefined,
  preparationCosts: undefined,
  implementationCosts: undefined,
  fundingMainSourceEnumerationId: undefined,
  notes: '',
  keyWord: '',
  operationalProgram: '',
  priorityAxis: '',
  investmentPriority: '',
  territorialDimension: '',
  european: 0,
  stateBudget: 0,
  otherForeignSources: 0,
  lumpSum: 0,
  ownResourcesIneligibleAmount: 0,
  ownResources: 0,
  updated: undefined,
  updatedBy: '',
  createdBy: '',
  priority: false,
  financingMethod: undefined,
  fundingInputMode: FundingInputMode.PERCENT,
  projectPreparationApproval: false,
  projectPreparationResolution: '',
  otherTypeApproval: false,
  otherTypeResolution: '',
  financialApproval: false,
  financialApprovalResolution: '',
  legalAct: false,
  legalActDate: undefined
};

export function getProjectCardValues(project?: ProjectResponseDto): ProjectCardFormValues {
  if (!project) {
    return projectCardDefaultValues;
  }
  const filteredEmailList = project.contactEmails
    ? project?.contactEmails?.split(';').map((value) => ({ email: value, key: randomId() }))
    : [{ email: '', key: randomId() }];
  return {
    name: project.name,
    registrationNumber: project.registrationNumber,
    eventNumber: project.eventNumber,
    holder: project.holder,
    holderIc: project.holderIc,
    holderLegalFormEnumerationId: project.holderLegalForm
      ? project.holderLegalForm.id + ''
      : undefined,
    guarantor: project.guarantor,
    contactPerson: project.contactPerson,
    contactEmails: filteredEmailList,
    indicators: project.indicators?.map((indicatorDto) => ({
      key: randomId(),
      id: indicatorDto.id,
      description: indicatorDto.description,
      measuringUnit: indicatorDto.measuringUnit,
      value: indicatorDto.value
    })),
    manager: project.manager,
    briefDescription: project.briefDescription,
    partners: project.partners,
    totalCosts: project.totalCosts,
    followUpPrkkPriorityAreaId: project.followUpPrkkPriorityArea
      ? project.followUpPrkkPriorityArea.id + ''
      : undefined,
    followUpPrkkSubregionId: project.followUpPrkkSubregion
      ? project.followUpPrkkSubregion.id + ''
      : undefined,
    followUpPrkkSpecificTargetId: project.followUpPrkkSpecificTarget
      ? project.followUpPrkkSpecificTarget.id + ''
      : undefined,
    followUpPrkkMeasureId: project.followUpPrkkMeasure
      ? project.followUpPrkkMeasure.id + ''
      : undefined,
    followUpSrrcrStrategicTargetId: project.followUpSrrcrStrategicTarget
      ? project.followUpSrrcrStrategicTarget.id + ''
      : undefined,
    followUpSrrcrSpecificTargetId: project.followUpSrrcrSpecificTarget
      ? project.followUpSrrcrSpecificTarget.id + ''
      : undefined,
    followUpSrrcrTypeMeasureId: project.followUpSrrcrTypeMeasure
      ? project.followUpSrrcrTypeMeasure.id + ''
      : undefined,
    actionPlanEnumerationId: project.actionPlan ? project.actionPlan?.id + '' : undefined,
    developmentStateEnumerationId: project.developmentState
      ? project.developmentState?.id + ''
      : undefined,
    result: project.result,
    resultQuantification: project.resultQuantification,
    typeEnumerationId: project.type ? project.type?.id + '' : undefined,
    realizationPlace: project.realizationPlace,
    preprojectPreparationStart: stringToDate(project.preprojectPreparationStart),
    implementationLength: project.implementationLength,
    territorialDecisionEnumerationId: project.territorialDecision
      ? project.territorialDecision?.id + ''
      : undefined,
    financialEnd: stringToDate(project.financialEnd),
    buildingPermitEnumerationId: project.buildingPermit
      ? project.buildingPermit?.id + ''
      : undefined,
    sustainabilityPeriodStart: stringToDate(project.sustainabilityPeriodStart),
    sustainabilityPeriodEnd: stringToDate(project.sustainabilityPeriodEnd),
    implementationStart: stringToDate(project.implementationStart) || new Date(),
    implementationEnd: stringToDate(project.implementationEnd) || new Date(),
    initiativeEnumerationId: project.initiative ? project.initiative?.id + '' : undefined,
    totalEstimatedCosts: project.totalEstimatedCosts,
    preparationCosts: project.preparationCosts,
    implementationCosts: project.implementationCosts,
    fundingMainSourceEnumerationId: project.fundingMainSource
      ? project.fundingMainSource?.id + ''
      : undefined,
    notes: project.notes,
    keyWord: project.keyWord,
    operationalProgram: project.operationalProgram,
    priorityAxis: project.priorityAxis,
    investmentPriority: project.investmentPriority,
    territorialDimension: project.territorialDimension,
    european: project.european,
    stateBudget: project.stateBudget,
    otherForeignSources: project.otherForeignSources,
    lumpSum: project.lumpSum,
    ownResourcesIneligibleAmount: project.ownResourcesIneligibleAmount ?? 0,
    ownResources: project.ownResources,
    updated: stringToDateTime(project.updated),
    updatedBy: project.updatedBy
      ? project.updatedBy?.firstName + ' ' + project.updatedBy?.lastName
      : project.updatedByLegacy,
    createdBy: project.createdBy
      ? project.createdBy?.firstName + ' ' + project.createdBy?.lastName
      : project.createdByLegacy,
    priority: project.priority,
    financingMethod: project.financingMethod,
    fundingInputMode: (project.fundingInputMode as FundingInputMode) ?? FundingInputMode.PERCENT,
    projectPreparationApproval: project.projectPreparationApproval ?? false,
    projectPreparationResolution: project.projectPreparationResolution ?? '',
    otherTypeApproval: project.otherTypeApproval ?? false,
    otherTypeResolution: project.otherTypeResolution ?? '',
    financialApproval: project.financialApproval ?? false,
    financialApprovalResolution: project.financialApprovalResolution ?? '',
    legalAct: project.legalAct ?? false,
    legalActDate: stringToDate(project.legalActDate)
  };
}

export const [ProjectCardFormProvider, useUserFormContext, useProjectCardForm] =
  createFormContext<ProjectCardFormValues>();

// Kompletní sada typů výdajů, kterou musí mít každý rok v tabulce výdajů.
// DAP-017: rok podložený neúplným (nebo prázdným) polem položek nešel editovat —
// zadaná hodnota se tiše zahodila a nepřepočetla se dotace.
export const ALL_EXPENSE_TYPES: ExpensesType[] = [
  ExpensesType.ConsiderableExpenses,
  ExpensesType.IneligibleExpenses,
  ExpensesType.BudgetEntitlement,
  ExpensesType.OperatingCosts,
  ExpensesType.SustainabilityCosts,
  ExpensesType.SubsidyReturn,
  ExpensesType.ScheduledDepositsReceived,
  ExpensesType.Investment,
  ExpensesType.NonInvestment
];

// DAP-017: doplní do pole položek roku chybějící typy výdajů jako nulové položky (bez id),
// aby měl každý rok vždy kompletní sadu a každá buňka tabulky měla co aktualizovat.
function fillMissingExpenseTypes(year: number, yearData: ProjectExpenseDto[]): ProjectExpenseDto[] {
  const presentTypes = new Set(yearData.map((expense) => expense.expenseType));
  const missingTypes = ALL_EXPENSE_TYPES.filter((type) => !presentTypes.has(type));
  if (missingTypes.length === 0) {
    return yearData;
  }
  return [...yearData, ...missingTypes.map((type) => ({ year, expense: 0, expenseType: type }))];
}

function getYearDataMap(
  years: number[],
  considerableExpenses: ProjectExpenseDto[],
  ineligibleExpenses: ProjectExpenseDto[],
  budgetEntitlement: ProjectExpenseDto[],
  operatingCosts: ProjectExpenseDto[],
  sustainabilityCosts: ProjectExpenseDto[],
  subsidyReturn: ProjectExpenseDto[],
  scheduledDepositsReceived: ProjectExpenseDto[],
  investment: ProjectExpenseDto[],
  nonInvestment: ProjectExpenseDto[]
) {
  const yearDataMap = new Map<number, ProjectExpenseDto[]>();

  years.forEach((year) => {
    const yearData = [
      ...considerableExpenses.filter((expense) => expense.year === year),
      ...ineligibleExpenses.filter((expense) => expense.year === year),
      ...budgetEntitlement.filter((expense) => expense.year === year),
      ...operatingCosts.filter((expense) => expense.year === year),
      ...sustainabilityCosts.filter((expense) => expense.year === year),
      ...subsidyReturn.filter((expense) => expense.year === year),
      ...scheduledDepositsReceived.filter((expense) => expense.year === year),
      ...investment.filter((expense) => expense.year === year),
      ...nonInvestment.filter((expense) => expense.year === year)
    ];

    // DAP-017: rok v zobrazovaném rozsahu nemusí mít v DB žádné (nebo všechny) řádky —
    // bez doplnění nulových položek by takový rok nešel editovat.
    yearDataMap.set(year, fillMissingExpenseTypes(year, yearData));
  });
  return yearDataMap;
}

// DAP-017: zajistí, že mapa výdajů obsahuje všechny požadované roky a že každý rok
// (včetně už existujících) má kompletní sadu typů výdajů. Pokud není co doplnit,
// vrací původní instanci mapy, aby zbytečně nespouštěla re-render.
export function fillMissingExpenseYears(
  prev: Map<number, ProjectExpenseDto[]>,
  requiredYears: number[]
): Map<number, ProjectExpenseDto[]> {
  const allYears = [...new Set([...prev.keys(), ...requiredYears])].sort((a, b) => a - b);

  const needsUpdate = allYears.some((year) => {
    const presentTypes = new Set((prev.get(year) ?? []).map((expense) => expense.expenseType));
    return ALL_EXPENSE_TYPES.some((type) => !presentTypes.has(type));
  });
  if (!needsUpdate) {
    return prev;
  }

  const updated = new Map<number, ProjectExpenseDto[]>();
  allYears.forEach((year) => {
    updated.set(year, fillMissingExpenseTypes(year, prev.get(year) ?? []));
  });
  return updated;
}

// DAP-017: aktualizuje (nebo vytvoří, pokud chybí) položku daného typu výdaje pro daný rok.
// Dřívější implementace položku při chybějícím typu tiše zahodila — hodnota se pak
// nepropsala do stavu, nepřepočetla se dotace a nic se neuložilo.
function setYearExpense(
  yearData: ProjectExpenseDto[],
  year: number,
  expensesType: ExpensesType,
  value: number | string
): ProjectExpenseDto[] {
  // [DAP-032] Normalizace na číslo — do value se z Mantine NumberInput může dostat řetězec.
  const numericValue = toExpenseNumber(value);
  const index = yearData.findIndex((expense) => expense.expenseType === expensesType);
  if (index === -1) {
    return [...yearData, { year, expense: numericValue, expenseType: expensesType }];
  }
  const updated = [...yearData];
  updated[index] = { ...updated[index], expense: numericValue };
  return updated;
}

// DAP-017: čistá funkce pro změnu hodnoty v tabulce výdajů. Kromě samotné změny
// udržuje BUDGET_ENTITLEMENT jako součet INVESTMENT + NON_INVESTMENT daného roku.
export function applyExpenseChange(
  yearData: ProjectExpenseDto[],
  year: number,
  value: number | string,
  expensesType: string
): ProjectExpenseDto[] {
  let updatedYearData = setYearExpense(yearData, year, expensesType as ExpensesType, value);

  if (expensesType === ExpensesType.Investment || expensesType === ExpensesType.NonInvestment) {
    // [DAP-032] Bez normalizace by se tu částky zřetězily a chybný BUDGET_ENTITLEMENT
    // by se uložil do databáze.
    const investment = toExpenseNumber(
      updatedYearData.find((expense) => expense.expenseType === ExpensesType.Investment)?.expense
    );
    const nonInvestment = toExpenseNumber(
      updatedYearData.find((expense) => expense.expenseType === ExpensesType.NonInvestment)?.expense
    );
    updatedYearData = setYearExpense(
      updatedYearData,
      year,
      ExpensesType.BudgetEntitlement,
      investment + nonInvestment
    );
  }

  return updatedYearData;
}

export function getDefaultYearlyExpensesDataMap(
  preprojectPreparationStart?: Date | string,
  implementationStart?: Date | string,
  implementationEnd?: Date | string
) {
  const years: number[] = getExpensesYears(
    preprojectPreparationStart,
    implementationStart,
    implementationEnd
  );
  let considerableExpenses: ProjectExpenseDto[] = [];
  let ineligibleExpenses: ProjectExpenseDto[] = [];
  let budgetEntitlement: ProjectExpenseDto[] = [];
  let operatingCosts: ProjectExpenseDto[] = [];
  let sustainabilityCosts: ProjectExpenseDto[] = [];
  let subsidyReturn: ProjectExpenseDto[] = [];
  let scheduledDepositsReceived: ProjectExpenseDto[] = [];
  let investment: ProjectExpenseDto[] = [];
  let nonInvestment: ProjectExpenseDto[] = [];
  years.forEach((year) => {
    considerableExpenses.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.ConsiderableExpenses
    });
    ineligibleExpenses.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.IneligibleExpenses
    });
    budgetEntitlement.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.BudgetEntitlement
    });
    operatingCosts.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.OperatingCosts
    });
    sustainabilityCosts.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.SustainabilityCosts
    });
    subsidyReturn.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.SubsidyReturn
    });
    scheduledDepositsReceived.push({
      year: year,
      expense: 0,
      expenseType: ExpensesType.ScheduledDepositsReceived
    });
    investment.push({
      year,
      expense: 0,
      expenseType: ExpensesType.Investment
    });
    nonInvestment.push({
      year,
      expense: 0,
      expenseType: ExpensesType.NonInvestment
    });
  });

  return getYearDataMap(
    years,
    considerableExpenses,
    ineligibleExpenses,
    budgetEntitlement,
    operatingCosts,
    sustainabilityCosts,
    subsidyReturn,
    scheduledDepositsReceived,
    investment,
    nonInvestment
  );
}

export function getYearlyExpensesDataMap(project?: ProjectResponseDto) {
  const existingYears: number[] = [
    ...new Set(project?.expenses?.map((expense) => expense.year as number))
  ];
  const dateBasedYears = getExpensesYears(
    project?.preprojectPreparationStart,
    project?.implementationStart,
    project?.implementationEnd
  );
  // [DAP-040] Roky 2. tabulky — od přípravy/realizace do ukončení udržitelnosti, takže mapa obsahuje
  // i rok mezi koncem realizace a začátkem udržitelnosti.
  const sustainabilityYears =
    getSustainabilityTableYears(
      project?.preprojectPreparationStart,
      project?.implementationStart,
      project?.sustainabilityPeriodStart,
      project?.sustainabilityPeriodEnd
    ) ?? [];
  const years: number[] = [
    ...new Set([...existingYears, ...dateBasedYears, ...sustainabilityYears])
  ];
  let considerableExpenses: ProjectExpenseDto[] =
    project?.expenses?.filter(
      (expense) => ExpensesType.ConsiderableExpenses == expense.expenseType
    ) || [];
  let ineligibleExpenses: ProjectExpenseDto[] =
    project?.expenses?.filter(
      (expense) => ExpensesType.IneligibleExpenses == expense.expenseType
    ) || [];
  let budgetEntitlement: ProjectExpenseDto[] =
    project?.expenses?.filter((expense) => ExpensesType.BudgetEntitlement == expense.expenseType) ||
    [];
  let operatingCosts: ProjectExpenseDto[] =
    project?.expenses?.filter((expense) => ExpensesType.OperatingCosts == expense.expenseType) ||
    [];
  let sustainabilityCosts: ProjectExpenseDto[] =
    project?.expenses?.filter(
      (expense) => ExpensesType.SustainabilityCosts == expense.expenseType
    ) || [];
  let subsidyReturn: ProjectExpenseDto[] =
    project?.expenses?.filter((expense) => ExpensesType.SubsidyReturn == expense.expenseType) || [];
  let scheduledDepositsReceived: ProjectExpenseDto[] =
    project?.expenses?.filter(
      (expense) => ExpensesType.ScheduledDepositsReceived == expense.expenseType
    ) || [];
  let investment: ProjectExpenseDto[] =
    project?.expenses?.filter((expense) => ExpensesType.Investment == expense.expenseType) || [];
  let nonInvestment: ProjectExpenseDto[] =
    project?.expenses?.filter((expense) => ExpensesType.NonInvestment == expense.expenseType) || [];
  return getYearDataMap(
    years.sort((a, b) => a - b),
    considerableExpenses,
    ineligibleExpenses,
    budgetEntitlement,
    operatingCosts,
    sustainabilityCosts,
    subsidyReturn,
    scheduledDepositsReceived,
    investment,
    nonInvestment
  );
}

/**
 * [DAP-034] Částky porovnáváme v celých tisícinách koruny, ne v plovoucí čárce. Součet mnoha
 * `number` hodnot akumuluje chybu IEEE-754 (`0.1 + 0.2 = 0.30000000000000004`), takže by projekt
 * s výdaji přesně na výši nákladů vyšel jako překročení. Dřív to řešila tolerance `0.005`, kterou
 * bylo nutné držet shodnou s konstantou na backendu; porovnání v celých číslech ji činí zbytečnou.
 *
 * Faktor 1000 plyne z `decimalScale={3}` na číselných vstupech (CustomNumberInput.tsx,
 * CustomNumberInputWithValue.tsx) — jemněji než na tisícinu koruny nejde částku v kartě zadat.
 * `Math.round` je v tomhle rozsahu přesný do ~9e12 Kč, reálné částky jsou o tři řády níž.
 *
 * Pozn.: backend porovnává přesně (BigDecimal), zatímco tady kvantizujeme, takže payload se 4+
 * desetinnými místy poslaný mimo UI projede frontendem a spadne až na backendu. Přes kartu je
 * nedosažitelný a v datech takové částky nejsou (ověřeno 2026-09: max 2 desetinná místa).
 */
const toMillikoruna = (value: unknown): number => Math.round(toExpenseNumber(value) * 1000);

export const TOTAL_EXPENSES_EXCEEDED_ERROR =
  'Celkové výdaje (uznatelné + neuznatelné) nesmí přesáhnout celkové (předpokládané) ' +
  'náklady projektu';

/**
 * [DAP-031] „Celkové výdaje" = uznatelné výdaje celkem + neuznatelné výdaje celkem
 * přes všechny roky. Počítá se přímo z `combinedExpenses` (zdroj pravdy pro ukládaný DTO).
 */
export const sumTotalExpenses = (combinedExpenses: Map<number, ProjectExpenseDto[]>): number =>
  Array.from(combinedExpenses.values())
    .flat()
    .filter(
      (expense) =>
        expense.expenseType === ExpensesType.ConsiderableExpenses ||
        expense.expenseType === ExpensesType.IneligibleExpenses
    )
    .reduce((sum, expense) => sum + toExpenseNumber(expense.expense), 0);

/**
 * [DAP-031] Vrací true, pokud „Celkové výdaje" přesahují „Celkové (předpokládané) náklady
 * projektu". Když nejsou náklady vyplněné, pravidlo se neuplatní (vyplnění hlídá jiná validace).
 */
/**
 * [DAP-035] „Celkem" v rozpadu financování = částky ze čtyř procentních zdrojů + ručně zadané
 * „Vlastní zdroje (nezpůsobilé výdaje)". Paušál (lumpSum) se do součtu NEzapočítává.
 *
 * [DAP-032] Ručně zadanou částku normalizujeme přes toExpenseNumber — z Mantine NumberInput
 * může přijít řetězec (`''` po vymazání pole) a `+` by ho zřetězil místo sečtení.
 */
export const sumFundingBreakdown = (amounts: {
  europeanTotal: number;
  stateBudgetTotal: number;
  otherForeignSourcesTotal: number;
  ownResourcesTotal: number;
  ownResourcesIneligibleAmount?: number;
}): number =>
  amounts.europeanTotal +
  amounts.stateBudgetTotal +
  amounts.otherForeignSourcesTotal +
  amounts.ownResourcesTotal +
  toExpenseNumber(amounts.ownResourcesIneligibleAmount);

export const exceedsTotalEstimatedCosts = (
  totalExpenses: number,
  totalEstimatedCosts?: number
): boolean =>
  totalEstimatedCosts !== undefined &&
  totalEstimatedCosts !== null &&
  toMillikoruna(totalExpenses) > toMillikoruna(totalEstimatedCosts);
